import axios from "axios"
import { toast } from "sonner"

// 创建axios实例
const request = axios.create({
  baseURL: '/idata',
  timeout: 60000,
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
  },
})

// 存储正在进行的请求
const pendingRequests = new Map()

// 生成请求的唯一标识
const generateRequestKey = (config) => {
  const { method, url, params, data } = config
  return `${method}:${url}:${JSON.stringify(params)}:${JSON.stringify(data)}`
}

// 取消重复请求
const cancelDuplicateRequest = (config) => {
  const requestKey = generateRequestKey(config)

  // 如果存在相同的请求，取消之前的请求
  if (pendingRequests.has(requestKey)) {
    const controller = pendingRequests.get(requestKey)
    controller.abort()
    pendingRequests.delete(requestKey)
  }

  // 创建新的AbortController
  const controller = new AbortController()
  config.signal = controller.signal
  pendingRequests.set(requestKey, controller)

  return requestKey
}

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // 取消重复请求
    const requestKey = cancelDuplicateRequest(config)
    config.requestKey = requestKey

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    const { config, data, status } = response
    // 请求完成后从pending列表中移除
    if (config.requestKey && pendingRequests.has(config.requestKey)) {
      pendingRequests.delete(config.requestKey)
    }
    if (config.url === '/idatausa-service/api/products/check') {
      return status;
    }
    // 处理业务逻辑
    if (data && typeof data === "object") {
      // 如果code为1，返回数据
      if (data.code === 1) {
        return data.data || data
      } else {
        // code不为1时，显示错误提示
        const errorMessage = data.subMsg || data.message || "请求失败"
        toast.error(errorMessage)
        return Promise.reject(new Error(errorMessage))
      }
    }

    return data
  },
  (error) => {
    const { config } = error

    // 请求完成后从pending列表中移除
    if (config?.requestKey && pendingRequests.has(config.requestKey)) {
      pendingRequests.delete(config.requestKey)
    }

    // 如果是取消请求，不显示错误提示
    if (axios.isCancel(error) || error.name === "AbortError") {
      console.log("Request cancelled:", error.message)
      return Promise.reject(error)
    }

    // 处理HTTP错误
    let errorMessage = "网络请求失败"

    if (error.response) {
      // 服务器返回错误状态码
      const { status, data } = error.response

      switch (status) {
        case 400:
          errorMessage = data?.subMsg || "请求参数错误"
          break
        case 401:
          errorMessage = "未授权，请重新登录"
          // 可以在这里处理登录跳转
          break
        case 403:
          errorMessage = "拒绝访问"
          break
        case 404:
          errorMessage = "请求的资源不存在"
          break
        case 500:
          errorMessage = "服务器内部错误"
          break
        default:
          errorMessage = data?.subMsg || `请求失败 (${status})`
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      errorMessage = "网络连接超时，请检查网络"
    }
    return Promise.reject(error)
  },
)

// GET请求
export const get = (url, params = {}, config = {}) => {
  return request({
    method: "GET",
    url,
    params,
    ...config,
  })
}

// POST请求
export const post = (url, data = {}, config = {}) => {
  return request({
    method: "POST",
    url,
    data,
    ...config,
  })
}


// 导出axios实例，以便需要时直接使用
export default request